import { FC, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Box, ButtonBase, GlobalStyles, IconButton, Slide, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useConsentStore } from 'store/consentStore'
import { PartnerPageData } from 'types/types'
import { DESKTOP_QUERY, MOBILE_QUERY } from '../bottomNavStyles'

const BANNER_BG = '#0F1E27'
const BANNER_BORDER = '#1a2c38'
const DESKTOP_BORDER = '#1e293b'
const ACCEPT_BG = '#36E0F6'
const SECONDARY_TEXT = '#94a3b8'
const SECONDARY_BORDER = '#334155'
const SECONDARY_HOVER_BORDER = '#64748b'

const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
// Matches the ticket's fallback until the banner has been measured.
const DEFAULT_HEIGHT = 130

const BUTTON_SX = {
  flex: 1,
  height: '44px',
  px: 2,
  borderRadius: '999px',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
}

interface CookieBannerProps {
  page: PartnerPageData
}

/**
 * TBL's cookie consent banner: full width under the mobile bottom nav (which it lifts by its
 * height through --cookie-banner-height), a floating card in the bottom-left on desktop.
 */
export const CookieBanner: FC<CookieBannerProps> = ({ page }) => {
  const isOpen = useConsentStore((state) => state.isBannerOpen)
  const choose = useConsentStore((state) => state.choose)
  const closeSettings = useConsentStore((state) => state.closeSettings)
  // Set when reopened from Cookie Settings: the visitor has already chosen, so they can close it
  // and keep that choice. A first-time visitor has to pick one of the two buttons.
  const current = useConsentStore((state) => state.consent)
  const bannerRef = useRef<HTMLElement>(null)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)

  useEffect(() => {
    if (!isOpen || !current) return undefined
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSettings()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, current, closeSettings])

  useEffect(() => {
    const banner = bannerRef.current
    if (!isOpen || !banner) return undefined
    // Border box, so a change to the safe-area padding alone (iOS Safari's toolbar showing or
    // hiding) re-measures too.
    const observer = new ResizeObserver(() => setHeight(banner.getBoundingClientRect().height))
    observer.observe(banner, { box: 'border-box' })
    return () => observer.disconnect()
  }, [isOpen])

  return (
    <>
      {/* Dropped as soon as a choice is made, so the nav slides down while the banner slides out. */}
      {isOpen && (
        <GlobalStyles
          styles={{
            [MOBILE_QUERY]: {
              body: {
                '--cookie-banner-height': `${height}px`,
                '--bottom-nav-safe-area': '0px',
              },
              'body:not(:has([data-bottom-nav]))': { paddingBottom: `${height}px` },
            },
          }}
        />
      )}
      <Slide in={isOpen} direction="up" timeout={300} easing={EASING} mountOnEnter unmountOnExit>
        <Box
          ref={bannerRef}
          component="section"
          aria-labelledby="cookie-banner-title"
          sx={{
            position: 'fixed',
            zIndex: 1160,
            bgcolor: BANNER_BG,
            color: 'white',
            [MOBILE_QUERY]: {
              bottom: 0,
              left: 0,
              right: 0,
              px: 2,
              pt: 2,
              pb: 'calc(16px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${BANNER_BORDER}`,
            },
            [DESKTOP_QUERY]: {
              bottom: '24px',
              left: '24px',
              maxWidth: '448px',
              p: 2.5,
              borderRadius: '16px',
              border: `1px solid ${DESKTOP_BORDER}`,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            },
          }}
        >
          {current && (
            <IconButton
              onClick={closeSettings}
              aria-label="Close cookie settings"
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, color: SECONDARY_TEXT }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          <Typography
            id="cookie-banner-title"
            fontWeight={700}
            fontSize="0.9375rem"
            color="white"
            mb={0.5}
            pr={current ? 4 : 0}
          >
            🍪 Cookie Notice
          </Typography>
          <Typography fontSize="0.8125rem" lineHeight={1.5} color={SECONDARY_TEXT} mb={1.5}>
            We use cookies to enhance your experience, analyse site traffic, and deliver
            personalised competitions.{' '}
            <Box
              component={Link}
              href={`/${page.company_domain}/privacypol`}
              sx={{ color: 'white', textDecoration: 'underline' }}
            >
              Read our Cookie Policy.
            </Box>
          </Typography>
          {current && (
            <Typography fontSize="0.8125rem" color="white" fontWeight={600} mb={1.5}>
              Current setting: {current.analytics ? 'All cookies' : 'Essential only'}
            </Typography>
          )}
          <Box display="flex" gap={1.5}>
            <ButtonBase
              onClick={() => choose(false)}
              sx={{
                ...BUTTON_SX,
                bgcolor: 'transparent',
                color: SECONDARY_TEXT,
                border: `1px solid ${SECONDARY_BORDER}`,
                fontWeight: 600,
                transition: 'color 150ms, border-color 150ms',
                '&:hover': { color: 'white', borderColor: SECONDARY_HOVER_BORDER },
              }}
            >
              Essential Only
            </ButtonBase>
            <ButtonBase
              onClick={() => choose(true)}
              sx={{ ...BUTTON_SX, bgcolor: ACCEPT_BG, color: BANNER_BG, fontWeight: 700 }}
            >
              Accept All
            </ButtonBase>
          </Box>
        </Box>
      </Slide>
    </>
  )
}
